import {
  __,
  flip,
  any,
  ascend,
  defaultTo,
  descend,
  filter,
  includes,
  map,
  path,
  pick,
  evolve,
  pipe,
  prop,
  propEq,
  reject,
  reverse,
  sortWith,
  values,
  and,
  flatten
} from 'ramda'
import { createSelector } from 'reselect'
import { mergeSpec, headObj, lastObj, callOr } from '@/utils'
import toInteger from '@/utils/to-integer'

const usersSpec = users => ({
  users: {
    them: pipe(
      path(['user_ids', 'them']),
      defaultTo([]),
      pick(__, users),
      reject(and(propEq('first_name', 'Liv'), propEq('last_name', 'Support'))),
      values,
      map(
        evolve({
          allow_discovery: callOr(toInteger, null)
        })
      )
    ),
    us: pipe(path(['user_ids', 'us']), defaultTo([]), pick(__, users), values)
  }
})

const addUsersAndLastEventMessage = (users, lastEvents) =>
  mergeSpec({
    ...usersSpec(users),
    last_event_message: pipe(prop('timeline_room'), prop(__, lastEvents), defaultTo({}), lastObj)
  })

export const timelinesSelector = createSelector(
  path(['chat', 'timelines']),
  path(['chat', 'users']),
  path(['chat', 'lastEvents']),
  (timelines, users = {}, lastEvents) => {
    return pipe(
      values,
      reverse,
      sortWith([descend(prop('last_event'))]),
      map(addUsersAndLastEventMessage(users, lastEvents))
    )(timelines)
  }
)
export const timelinesSearchSelector = createSelector(
  path(['chat', 'timelines']),
  path(['chat', 'users']),
  path(['chat', 'eventsSearch']),
  (timelines, users = {}, eventsSearch = {}) => {
    if (!timelines) return []
    return pipe(
      values,
      map(values),
      flatten,
      map(
        pipe(
          last_event_message => ({
            last_event_message,
            ...timelines[last_event_message.timeline_room]
          }),
          mergeSpec({
            ...usersSpec(users),
            last_event: path(['last_event_message', 'created_at'])
          })
        )
      ),
      sortWith([descend(prop('last_event'))])
    )(eventsSearch)
  }
)

export const timelineSelector = createSelector(
  path(['chat', 'current_timeline']),
  path(['chat', 'users']),
  path(['chat', 'lastEvents']),
  (timeline, users = {}, lastEvents) => {
    return timeline && addUsersAndLastEventMessage(users, lastEvents)(timeline)
  }
)

export const listingTimelinesSelector = createSelector(
  timelinesSelector,
  flip(prop('listingId')),
  (timelines, listingId) => filter(propEq('listing_id', listingId), timelines)
)

export const tenantTimelinesSelector = createSelector(
  listingTimelinesSelector,
  (state, props) => path(['listing', props.listingId, 'contract', 'tenant_ids'], state),
  (timelines, tenantIds) =>
    filter(
      pipe(path(['users', 'them']), map(prop('id')), any(includes(__, defaultTo([], tenantIds)))),
      timelines
    )
)

export const timelineEventsSelector = createSelector(
  flip(prop('timelineId')),
  prop('events'),
  (timelineId, events) =>
    pipe(
      prop(timelineId),
      values,
      reject(propEq('event_type', 'chat_listing_match')),
      sortWith([ascend(prop('created_at')), ascend(prop('id'))])
    )(events)
)

export const callbackUserSelector = createSelector(
  prop('users'),
  path(['files', 'user']),
  (users, files) =>
    pipe(
      headObj,
      pick(['id', 'first_name', 'last_name', 'preferred_name', 'identity_verified_state']),
      mergeSpec({
        avatar: pipe(prop('id'), prop(__, files), headObj)
      })
    )(users)
)
